"use client";

import { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

interface TrueFalseCardProps {
  data: {
    statement: string;
    correct: boolean;
    explanation: string;
  };
  onAnswer: (correct: boolean) => void;
}

export function TrueFalseCard({ data, onAnswer }: TrueFalseCardProps) {
  const { statement, correct, explanation } = data;
  const [selected, setSelected] = useState<boolean | null>(null);
  const [submitted, setSubmitted] = useState(false);

  function handleSubmit() {
    if (selected === null) return;
    setSubmitted(true);
    onAnswer(selected === correct);
  }

  function getButtonClasses(value: boolean) {
    const base =
      "flex-1 rounded-lg border p-4 text-center font-semibold transition";

    if (submitted) {
      if (value === correct) {
        return cn(base, "bg-green-100 border-green-500 text-green-800");
      }
      if (value === selected && value !== correct) {
        return cn(base, "bg-red-100 border-red-500 text-red-800");
      }
      return cn(base, "opacity-50");
    }

    if (selected === value) {
      return cn(base, "border-primary ring-2 ring-primary/20 cursor-pointer");
    }

    return cn(base, "cursor-pointer hover:border-zinc-400");
  }

  return (
    <div className="flex flex-col">
      <p className="text-lg font-medium mb-6">{statement}</p>

      <div className="flex flex-row gap-4">
        <button
          type="button"
          className={getButtonClasses(true)}
          onClick={() => !submitted && setSelected(true)}
          disabled={submitted}
        >
          True
        </button>
        <button
          type="button"
          className={getButtonClasses(false)}
          onClick={() => !submitted && setSelected(false)}
          disabled={submitted}
        >
          False
        </button>
      </div>

      {submitted && (
        <div className="bg-muted rounded-lg p-4 mt-4">
          <p className="text-sm leading-relaxed">{explanation}</p>
        </div>
      )}

      {!submitted && (
        <Button
          className="w-full mt-6"
          disabled={selected === null}
          onClick={handleSubmit}
        >
          Check
        </Button>
      )}
    </div>
  );
}
